import {useLingui} from '@lingui/react/macro';
import {useDispatch, useSelector} from 'react-redux';
import {useCallback} from 'react';
import {StyleSheet, View, Pressable} from 'react-native';
import {Svg, Path, Rect} from 'react-native-svg';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {selectors} from 'store/slices/files';
import {slices} from 'store';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';

interface LayoutSelectorProps {
  isShared?: boolean;
}

export function LayoutSelector(props: LayoutSelectorProps) {
  const {t} = useLingui();
  const dispatch = useDispatch();
  const layout = useSelector(selectors.getLayout);

  const handleLayoutChange = useCallback((newLayout: FileListItemLayout) => {
    dispatch(slices.files.actions.setLayout({layout: newLayout}));
  }, [dispatch]);

  // If in GridSimple, automatically switch to GridSmall
  if (layout === FileListItemLayout.GridSimple) {
    handleLayoutChange(FileListItemLayout.GridSmall);
  }

  // Color scheme based on context and theme
  const selectorTheme = props.isShared 
    ? theme.files.thumbnailSelector.shared 
    : theme.files.thumbnailSelector.myFiles;
  
  const iconColor = selectorTheme.icon;
  const activeIconColor = selectorTheme.activeIcon;
  const backgroundColor = selectorTheme.background;
  const activeBackgroundColor = selectorTheme.activeBackground;

  const layouts = [
    {
      type: FileListItemLayout.ListNormal,
      title: t`List view`,
      icon: (
        <Svg viewBox="0 0 24 24" width={20} height={20}>
          <Path d={vectors.icons.list} fill={iconColor} fillOpacity={0.7}/>
        </Svg>
      ),
    },
    {
      type: FileListItemLayout.GridSmall,
      title: t`Small thumbnails`,
      icon: (
        <Svg viewBox="0 0 24 24" width={20} height={20}>
          {/* 3x3 grid pattern for small */}
          <Rect x="2" y="2" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="9.5" y="2" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="17" y="2" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="2" y="9.5" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="9.5" y="9.5" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="17" y="9.5" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="2" y="17" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="9.5" y="17" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="17" y="17" width="5" height="5" fill={iconColor} fillOpacity={0.7}/>
        </Svg>
      ),
    },
    {
      type: FileListItemLayout.GridMedium,
      title: t`Medium thumbnails`,
      icon: (
        <Svg viewBox="0 0 24 24" width={20} height={20}>
          {/* 2x2 grid pattern for medium */}
          <Rect x="3" y="3" width="8" height="8" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="13" y="3" width="8" height="8" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="3" y="13" width="8" height="8" fill={iconColor} fillOpacity={0.7}/>
          <Rect x="13" y="13" width="8" height="8" fill={iconColor} fillOpacity={0.7}/>
        </Svg>
      ),
    },
    {
      type: FileListItemLayout.GridLarge,
      title: t`Large thumbnails`,
      icon: (
        <Svg viewBox="0 0 24 24" width={20} height={20}>
          {/* Single large square for large */}
          <Rect x="4" y="4" width="16" height="16" fill={iconColor} fillOpacity={0.7}/>
        </Svg>
      ),
    },
  ];

  return (
    <View style={[styles.container, {backgroundColor}]}>
      {layouts.map((item, index) => {
        const isActive = layout === item.type;
        const activeIcon = isActive ? (
          item.type === FileListItemLayout.ListNormal ? (
            <Svg viewBox="0 0 24 24" width={20} height={20}>
              <Path d={vectors.icons.list} fill={activeIconColor} fillOpacity={1}/>
            </Svg>
          ) : item.type === FileListItemLayout.GridSmall ? (
            <Svg viewBox="0 0 24 24" width={20} height={20}>
              <Rect x="2" y="2" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="9.5" y="2" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="17" y="2" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="2" y="9.5" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="9.5" y="9.5" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="17" y="9.5" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="2" y="17" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="9.5" y="17" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="17" y="17" width="5" height="5" fill={activeIconColor} fillOpacity={1}/>
            </Svg>
          ) : item.type === FileListItemLayout.GridMedium ? (
            <Svg viewBox="0 0 24 24" width={20} height={20}>
              <Rect x="3" y="3" width="8" height="8" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="13" y="3" width="8" height="8" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="3" y="13" width="8" height="8" fill={activeIconColor} fillOpacity={1}/>
              <Rect x="13" y="13" width="8" height="8" fill={activeIconColor} fillOpacity={1}/>
            </Svg>
          ) : (
            <Svg viewBox="0 0 24 24" width={20} height={20}>
              <Rect x="4" y="4" width="16" height="16" fill={activeIconColor} fillOpacity={1}/>
            </Svg>
          )
        ) : item.icon;
        
        return (
          <Pressable
            key={item.type}
            style={[
              styles.button,
              isActive && styles.activeButton,
              isActive && {backgroundColor: activeBackgroundColor}
            ]}
            onPress={() => handleLayoutChange(item.type)}
            accessibilityLabel={item.title}
            accessibilityRole="button"
          >
            {isActive ? activeIcon : item.icon}
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    borderRadius: 18,
    paddingHorizontal: 4,
    marginHorizontal: 8,
  },
  button: {
    width: 36,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 14,
    marginHorizontal: 2,
  },
  activeButton: {
    transform: [{scale: 1.05}],
  },
}); 